'use client'

import type { Zimmer } from '@/payload-types'

import React, { useRef } from 'react'
import RichText from '@/components/RichText'

import { motion, useScroll, useTransform } from 'motion/react'
import { ZimmerGallery } from './ZimmerGallery'
import AnimatedText from '@/components/Antonie/Components/animated-text'
import { useInView } from 'react-intersection-observer'
import { TypedLocale } from 'payload'

export const ZimmerBlockClient: React.FC<{
  zimmer: Zimmer[]
  title?: string | null
  introContent?: any
  locale: TypedLocale
}> = (props) => {
  const { zimmer, title, introContent, locale } = props
  const sectionRef = useRef(null)
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  })

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '5%'])

  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.4,
      },
    },
  }

  return (
    <motion.div ref={sectionRef} style={{ y }} className="container mx-auto px-6">
      {title && (
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={titleVariants}
          className="mb-20 text-center"
        >
          <AnimatedText
            text={title}
            type="h2"
            variant="simple"
            classNameText="text-2xl md:text-4xl"
            className="mb-8 font-heading text-2xl font-medium tracking-wide text-gray-900 md:text-4xl"
          />
          <div className="mx-auto mb-8 h-px w-16 bg-antonie-red" />
        </motion.div>
      )}
      {introContent && (
        <div className="container mb-16">
          <RichText className="ms-0 max-w-3xl" data={introContent} enableGutter={false} />
        </div>
      )}
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className=""
      >
        <ZimmerGallery zimmer={zimmer} locale={locale} />
      </motion.div>
    </motion.div>
  )
}
